import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Search, Car, UserCheck, UserX, LogIn, Loader2, CreditCard, Upload, ArrowUpDown, ArrowUp, ArrowDown, FileSpreadsheet, Layers } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { fetchCustomers, createCustomer, updateCustomer, deleteCustomer, fetchResellerProfile, createBillingPortalSession, Customer } from "@/lib/reseller-api";
import { fetchUsers, fetchDevices, createUser, loginAsUser, fetchPermissionsMap, userDevicesFromMap, linkDevice, unlinkDevice, createGroup, linkGroup } from "@/lib/traccar-api";
import { EditUserGroupsDialog } from "@/components/users/EditUserGroupsDialog";
import * as XLSX from 'xlsx';

const statusColors: Record<string, string> = {
  active: "bg-green-500",
  inactive: "bg-gray-500",
  suspended: "bg-red-500",
};

type TraccarMode = "none" | "existing" | "new";

export default function Customers() {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState<'name' | 'email' | 'phone' | 'tax_id' | 'status' | 'vehicles' | 'city' | 'town' | 'postal_code' | 'state' | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [isOpen, setIsOpen] = useState(false);
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [importRows, setImportRows] = useState<Partial<Customer>[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [manageDevicesCustomer, setManageDevicesCustomer] = useState<Customer | null>(null);
  const [deviceSearch, setDeviceSearch] = useState("");
  const [savingDevices, setSavingDevices] = useState(false);
  const [traccarMode, setTraccarMode] = useState<TraccarMode>("none");
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const editFormRef = useRef<HTMLFormElement>(null);
  const [selectedTraccarUserId, setSelectedTraccarUserId] = useState<string>("");
  const [newTraccarUser, setNewTraccarUser] = useState({ name: "", email: "", password: "" });
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: customers = [], isLoading } = useQuery({
    queryKey: ["customers"],
    queryFn: fetchCustomers,
  });

  const { data: profile } = useQuery({
    queryKey: ["reseller-profile"],
    queryFn: fetchResellerProfile,
  });

  const billingPortalMutation = useMutation({
    mutationFn: (customerId: string) => createBillingPortalSession(customerId, window.location.href),
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  const { data: traccarUsers = [] } = useQuery({
    queryKey: ["traccar-users"],
    queryFn: fetchUsers,
  });

  const traccarUserIds = customers
    .filter((c) => c.traccar_user_id)
    .map((c) => c.traccar_user_id!);

  const { data: allDevices = [] } = useQuery({
    queryKey: ["devices"],
    queryFn: fetchDevices,
  });

  const { data: permsMap } = useQuery({
    queryKey: ["permissions-map"],
    queryFn: fetchPermissionsMap,
  });

  const { data: deviceCounts = {} } = useQuery({
    queryKey: ["customer-device-counts", traccarUserIds, !!permsMap],
    queryFn: () => {
      const counts: Record<number, number> = {};
      for (const userId of traccarUserIds) {
        counts[userId] = userDevicesFromMap(userId, allDevices, permsMap!).length;
      }
      return counts;
    },
    enabled: traccarUserIds.length > 0 && allDevices.length > 0 && !!permsMap,
  });

  type ReportRow = { customer: string; deviceName: string; uniqueId: string; make: string; model: string };
  const { data: reportRows = [], isLoading: reportLoading } = useQuery<ReportRow[]>({
    queryKey: ["customer-vehicles-report", traccarUserIds, !!permsMap],
    queryFn: () => {
      const rows: ReportRow[] = [];
      for (const c of customers.filter(c => c.traccar_user_id)) {
        const devices = userDevicesFromMap(c.traccar_user_id!, allDevices, permsMap!);
        devices.forEach(d => rows.push({
          customer: c.name,
          deviceName: d.name,
          uniqueId: d.uniqueId,
          make: String(d.attributes?.make || ''),
          model: String(d.attributes?.vehicleModel || ''),
        }));
      }
      return rows.sort((a, b) => a.customer.localeCompare(b.customer) || a.deviceName.localeCompare(b.deviceName));
    },
    enabled: isReportOpen && traccarUserIds.length > 0 && allDevices.length > 0 && !!permsMap,
    staleTime: 60 * 1000,
  });

  const exportReportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(reportRows.map(r => ({
      'Customer': r.customer,
      'Vehicle': r.deviceName,
      'Device ID': r.uniqueId,
      'Make': r.make,
      'Model': r.model,
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Report');
    XLSX.writeFile(wb, 'customer-vehicles-report.xlsx');
  };

  const { data: userDevices = [], refetch: refetchUserDevices } = useQuery({
    queryKey: ["user-devices", manageDevicesCustomer?.traccar_user_id, !!permsMap],
    queryFn: () => userDevicesFromMap(manageDevicesCustomer!.traccar_user_id!, allDevices, permsMap!),
    enabled: !!manageDevicesCustomer?.traccar_user_id && !!permsMap && allDevices.length > 0,
  });

  const userDeviceIds = new Set(userDevices.map(d => d.id));

  const handleToggleDevice = async (deviceId: number, linked: boolean) => {
    if (!manageDevicesCustomer?.traccar_user_id) return;
    setSavingDevices(true);
    try {
      if (linked) {
        await unlinkDevice(manageDevicesCustomer.traccar_user_id, deviceId);
      } else {
        await linkDevice(manageDevicesCustomer.traccar_user_id, deviceId);
      }
      await queryClient.invalidateQueries({ queryKey: ["permissions-map"] });
      await refetchUserDevices();
      queryClient.invalidateQueries({ queryKey: ["customer-device-counts"] });
    } catch (error: any) {
      const msg: string = error.message || '';
      toast({ title: t("common.error"), description: msg.length > 120 ? msg.slice(0, 120) + '…' : msg, variant: "destructive" });
    } finally {
      setSavingDevices(false);
    }
  };

  const resetTraccarFields = () => {
    setTraccarMode("none");
    setSelectedTraccarUserId("");
    setNewTraccarUser({ name: "", email: "", password: "" });
  };

  const openDialog = (customer?: Customer) => {
    if (customer) {
      setEditingCustomer(customer);
      if (customer.traccar_user_id) {
        setTraccarMode("existing");
        setSelectedTraccarUserId(String(customer.traccar_user_id));
      } else {
        resetTraccarFields();
      }
    } else {
      setEditingCustomer(null);
      resetTraccarFields();
    }
    setIsOpen(true);
  };

  const createMutation = useMutation({
    mutationFn: createCustomer,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      setIsOpen(false);
      resetTraccarFields();
      toast({ title: t("reseller.customer.created") });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Customer> }) => updateCustomer(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      setIsOpen(false);
      setEditingCustomer(null);
      resetTraccarFields();
      toast({ title: t("reseller.customer.updated") });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteCustomer,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ["customers"]}).then();
      toast({ title: t("reseller.customer.deleted") });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  const loginAsMutation = useMutation({
    mutationFn: async (traccarUserId: number) => {
      const response = await loginAsUser(traccarUserId);
      if (!response.ok) throw new Error("Failed to login as user");
      return traccarUserId;
    },
    onSuccess: () => {
      window.location.href = "/";
    },
    onError: (error: Error) => {
      toast({ title: t("users.loginAsError"), description: error.message, variant: "destructive" });
    },
  });

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const SortIcon = ({ field }: { field: typeof sortField }) => {
    if (sortField !== field) return <ArrowUpDown className="ml-1 h-3 w-3 opacity-40" />;
    return sortDir === 'asc' ? <ArrowUp className="ml-1 h-3 w-3" /> : <ArrowDown className="ml-1 h-3 w-3" />;
  };

  const filteredCustomers = customers.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.email?.toLowerCase().includes(search.toLowerCase())
  ).sort((a, b) => {
    if (!sortField) return 0;
    let aVal: string | number, bVal: string | number;
    if (sortField === 'vehicles') {
      aVal = a.traccar_user_id ? (deviceCounts[a.traccar_user_id] ?? 0) : 0;
      bVal = b.traccar_user_id ? (deviceCounts[b.traccar_user_id] ?? 0) : 0;
    } else {
      aVal = (a[sortField] ?? '').toString().toLowerCase();
      bVal = (b[sortField] ?? '').toString().toLowerCase();
    }
    if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const data: Partial<Customer> = {
      name: formData.get("name") as string,
      email: formData.get("email") as string,
      phone: formData.get("phone") as string,
      tax_id: formData.get("tax_id") as string,
      customer_type: formData.get("customer_type") as Customer["customer_type"],
      status: formData.get("status") as Customer["status"],
      address: formData.get("address") as string,
      city: formData.get("city") as string,
      town: formData.get("town") as string,
      state: formData.get("state") as string,
      postal_code: formData.get("postal_code") as string,
      notes: formData.get("notes") as string,
    };

    try {
      if (traccarMode === "new") {
        setIsCreatingUser(true);
        try {
          const user = await createUser(newTraccarUser);
          data.traccar_user_id = user.id;
          const group = await createGroup({ name: data.name as string });
          await linkGroup(user.id, group.id);
          queryClient.invalidateQueries({ queryKey: ["traccar-users"] });
          queryClient.invalidateQueries({ queryKey: ["groups"] });
        } finally {
          setIsCreatingUser(false);
        }
      } else if (traccarMode === "existing" && selectedTraccarUserId) {
        data.traccar_user_id = Number(selectedTraccarUserId);
      } else {
        data.traccar_user_id = null;
      }

      if (editingCustomer) {
        updateMutation.mutate({ id: editingCustomer.id, data });
      } else {
        createMutation.mutate(data);
      }
    } catch (error: any) {
      const msg: string = error.message || '';
      toast({ title: t("common.error"), description: msg.length > 120 ? msg.slice(0, 120) + '…' : msg, variant: "destructive" });
    }
  };

  const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const raw = (ev.target?.result as string).replace(/^\uFEFF/, ""); // strip BOM
      const lines = raw.split(/\r?\n/).filter(Boolean);
      if (lines.length < 2) {
        toast({ title: t("common.error"), description: t("reseller.customer.importEmpty"), variant: "destructive" });
        return;
      }
      const delimiter = lines[0].includes("\t") ? "\t" : lines[0].includes(";") ? ";" : ",";
      const splitLine = (line: string) => line.split(delimiter).map((v) => v.trim().replace(/^"|"$/g, ""));
      const headers = splitLine(lines[0]).map((h) => h.toLowerCase().replace(/\s+/g, "_"));
      const rows = lines.slice(1).map((line) => {
        const values = splitLine(line);
        const row: Partial<Customer> = {};
        headers.forEach((header, i) => {
          (row as any)[header] = values[i] || null;
        });
        return row;
      }).filter((r) => r.name);
      if (rows.length === 0) {
        toast({ title: t("common.error"), description: t("reseller.customer.importEmpty"), variant: "destructive" });
        return;
      }
      setImportRows(rows);
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const handleImport = async () => {
    setIsImporting(true);
    let success = 0, failed = 0;
    for (const row of importRows) {
      try {
        const customerType = row.customer_type === "company" ? "company" : "person";
        await createCustomer({ ...row, customer_type: customerType, status: "active" });
        success++;
      } catch {
        failed++;
      }
    }
    setIsImporting(false);
    setIsImportOpen(false);
    setImportRows([]);
    queryClient.invalidateQueries({ queryKey: ["customers"] });
    toast({ title: t("reseller.customer.importDone", { success, failed }) });
  };

  const getTraccarUserLabel = (userId: number | null) => {
    if (!userId) return null;
    const user = traccarUsers.find((u) => u.id === userId);
    return user ? `${user.name} (${user.email})` : `#${userId}`;
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold"></h1>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-4">
              <span className="text-sm text-muted-foreground whitespace-nowrap">
                {filteredCustomers.length !== customers.length
                  ? t("reseller.customer.totalFiltered", { filtered: filteredCustomers.length, total: customers.length })
                  : t("reseller.customer.total", { count: customers.length })}
              </span>
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t("reseller.customer.searchPlaceholder")}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />

              </div>
              <Dialog open={isReportOpen} onOpenChange={setIsReportOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline"><FileSpreadsheet className="h-4 w-4 mr-2" />{t("reseller.customer.report")}</Button>
                </DialogTrigger>
                <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
                  <DialogHeader>
                    <DialogTitle>{t("reseller.customer.vehiclesReport")}</DialogTitle>
                  </DialogHeader>
                  <div className="flex justify-end mb-2 flex-shrink-0">
                    <Button variant="outline" size="sm" onClick={exportReportExcel} disabled={reportRows.length === 0}>
                      <FileSpreadsheet className="h-4 w-4 mr-2" />{t("devices.exportExcel")}
                    </Button>
                  </div>
                  {reportLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : (
                    <div className="overflow-y-auto flex-1 border rounded-md">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>{t("reseller.customer.name")}</TableHead>
                            <TableHead>{t("reseller.customer.vehicleName")}</TableHead>
                            <TableHead>{t("reseller.customer.make")}</TableHead>
                            <TableHead>{t("reseller.customer.model")}</TableHead>
                            <TableHead>{t("reseller.customer.vehicleId")}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {reportRows.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                                {t("reseller.customer.noVehiclesReport")}
                              </TableCell>
                            </TableRow>
                          ) : reportRows.map((row, i) => (
                            <TableRow key={i}>
                              <TableCell className="font-medium">{row.customer}</TableCell>
                              <TableCell>{row.deviceName}</TableCell>
                              <TableCell>{row.make}</TableCell>
                              <TableCell>{row.model}</TableCell>
                              <TableCell className="font-mono text-sm">{row.uniqueId}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </DialogContent>
              </Dialog>

              <Dialog open={isImportOpen} onOpenChange={(open) => { setIsImportOpen(open); if (!open) setImportRows([]); }}>
                <DialogTrigger asChild>
                  <Button variant="outline"><Upload className="h-4 w-4 mr-2" />{t("reseller.customer.import")}</Button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{t("reseller.customer.import")}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">{t("reseller.customer.importHint")}</p>
                    <code className="block text-xs bg-muted p-2 rounded">name,email,phone,tax_id,customer_type,address,city,town,state,postal_code,notes</code>
                    <Input type="file" accept=".csv,.tsv,.txt" onChange={handleImportFile} />
                    {importRows.length > 0 && (() => {
                      const cols = Object.keys(importRows[0]);
                      return (
                      <>
                        <p className="text-sm font-medium">{t("reseller.customer.importPreview", { count: importRows.length })}</p>
                        <div className="overflow-x-auto border rounded">
                          <table className="text-xs w-full">
                            <thead className="bg-muted">
                              <tr>
                                {cols.map((col) => (
                                  <th key={col} className="px-2 py-1 text-left whitespace-nowrap">{col}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {importRows.map((row, i) => (
                                <tr key={i} className="border-t">
                                  {cols.map((col) => (
                                    <td key={col} className="px-2 py-1 whitespace-nowrap">{(row as any)[col] || "-"}</td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <Button onClick={handleImport} disabled={isImporting} className="w-full">
                          {isImporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                          {t("reseller.customer.importConfirm", { count: importRows.length })}
                        </Button>
                      </>
                      );
                    })()}
                  </div>
                </DialogContent>
              </Dialog>
              <Dialog open={isOpen} onOpenChange={(open) => { setIsOpen(open); if (!open) { setEditingCustomer(null); resetTraccarFields(); } }}>
                <DialogTrigger asChild>
                  <Button onClick={() => openDialog()}><Plus className="h-4 w-4 mr-2" /> {t("reseller.customer.add")}</Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{editingCustomer ? t("reseller.customer.edit") : t("reseller.customer.add")}</DialogTitle>
                  </DialogHeader>
                  <form ref={editFormRef} onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">{t("reseller.customer.name")} *</Label>
                        <Input id="name" name="name" required defaultValue={editingCustomer?.name} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">{t("reseller.customer.email")} *</Label>
                        <Input id="email" name="email" type="email" required defaultValue={editingCustomer?.email || ""} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone">{t("reseller.customer.phone")}</Label>
                        <Input id="phone" name="phone" defaultValue={editingCustomer?.phone || ""} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="tax_id">{t("reseller.customer.taxId")}</Label>
                        <Input id="tax_id" name="tax_id" defaultValue={editingCustomer?.tax_id || ""} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="customer_type">{t("reseller.customer.customerType")}</Label>
                        <Select name="customer_type" defaultValue={editingCustomer?.customer_type || "person"}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="person">{t("reseller.customer.types.person")}</SelectItem>
                            <SelectItem value="company">{t("reseller.customer.types.company")}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="status">{t("reseller.customer.status")}</Label>
                        <Select name="status" defaultValue={editingCustomer?.status || "active"}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="active">{t("reseller.customer.statuses.active")}</SelectItem>
                            <SelectItem value="inactive">{t("reseller.customer.statuses.inactive")}</SelectItem>
                            <SelectItem value="suspended">{t("reseller.customer.statuses.suspended")}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="address">{t("reseller.customer.address")}</Label>
                        <Input id="address" name="address" defaultValue={editingCustomer?.address || ""} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="city">{t("reseller.customer.city")}</Label>
                        <Input id="city" name="city" defaultValue={editingCustomer?.city || ""} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="town">{t("reseller.customer.town")}</Label>
                        <Input id="town" name="town" defaultValue={editingCustomer?.town || ""} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="state">{t("reseller.customer.state")}</Label>
                        <Input id="state" name="state" defaultValue={editingCustomer?.state || ""} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="postal_code">{t("reseller.customer.postalCode")}</Label>
                        <Input id="postal_code" name="postal_code" defaultValue={editingCustomer?.postal_code || ""} />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="notes">{t("reseller.customer.notes")}</Label>
                      <Textarea id="notes" name="notes" defaultValue={editingCustomer?.notes || ""} />
                    </div>

                    {/* Traccar Login Section */}
                    <div className="space-y-2 border-t pt-4">
                      <Label>{t("reseller.customer.traccarLogin")}</Label>
                      <Select value={traccarMode} onValueChange={(v) => {
                        const mode = v as TraccarMode;
                        setTraccarMode(mode);
                        if (mode === 'new') {
                          const chars = 'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!@#$';
                          const password = Array.from(crypto.getRandomValues(new Uint8Array(12))).map(b => chars[b % chars.length]).join('');
                          setNewTraccarUser(p => ({
                            ...p,
                            name: editingCustomer?.name ?? '',
                            email: editingCustomer?.email ?? '',
                            password,
                          }));
                          setTimeout(() => {
                            const container = editFormRef.current?.parentElement;
                            if (container) container.scrollTop = container.scrollHeight;
                          }, 50);
                        }
                      }}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">{t("reseller.customer.unlinkUser")}</SelectItem>
                          <SelectItem value="existing">{t("reseller.customer.selectUser")}</SelectItem>
                          <SelectItem value="new">{t("reseller.customer.createNewUser")}</SelectItem>
                        </SelectContent>
                      </Select>

                      {traccarMode === "existing" && (
                        <Select value={selectedTraccarUserId} onValueChange={setSelectedTraccarUserId}>
                          <SelectTrigger>
                            <SelectValue placeholder={t("reseller.customer.selectUser")} />
                          </SelectTrigger>
                          <SelectContent>
                            {traccarUsers.map((user) => (
                              <SelectItem key={user.id} value={String(user.id)}>
                                {user.name} ({user.email})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}

                      {traccarMode === "new" && (
                        <div className="space-y-2">
                          <Input
                            placeholder={t("reseller.customer.name")}
                            value={newTraccarUser.name}
                            onChange={(e) => setNewTraccarUser((p) => ({ ...p, name: e.target.value }))}
                            required
                          />
                          <Input
                            placeholder={t("reseller.customer.email")}
                            type="email"
                            value={newTraccarUser.email}
                            onChange={(e) => setNewTraccarUser((p) => ({ ...p, email: e.target.value }))}
                            required
                          />
                          <Input
                            placeholder={t("reseller.customer.traccarPassword")}
                            type="text"
                            value={newTraccarUser.password}
                            onChange={(e) => setNewTraccarUser((p) => ({ ...p, password: e.target.value }))}
                            required
                          />
                        </div>
                      )}
                    </div>

                    <Button type="submit" className="w-full" disabled={isCreatingUser || createMutation.isPending || updateMutation.isPending}>
                      {(isCreatingUser || createMutation.isPending || updateMutation.isPending) && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      {editingCustomer ? t("common.update") : t("common.create")}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">{t("common.loading")}</div>
            ) : filteredCustomers.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">{t("reseller.customer.noCustomers")}</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="cursor-pointer select-none" onClick={() => handleSort('name')}><span className="flex items-center">{t("reseller.customer.name")}<SortIcon field="name" /></span></TableHead>
                    <TableHead className="cursor-pointer select-none" onClick={() => handleSort('email')}><span className="flex items-center">{t("reseller.customer.contact")}<SortIcon field="email" /></span></TableHead>
                    <TableHead className="cursor-pointer select-none" onClick={() => handleSort('tax_id')}><span className="flex items-center">{t("reseller.customer.taxId")}<SortIcon field="tax_id" /></span></TableHead>
                    <TableHead>{t("reseller.customer.traccarLogin")}</TableHead>
                    <TableHead className="cursor-pointer select-none" onClick={() => handleSort('vehicles')}><span className="flex items-center">{t("reseller.customer.vehicleCount")}<SortIcon field="vehicles" /></span></TableHead>
                    <TableHead>{t("reseller.common.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCustomers.map((customer) => (
                    <TableRow key={customer.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Badge className={statusColors[customer.status] + " text-[10px] px-1.5 py-0"}>{t(`reseller.customer.statuses.${customer.status}`)}</Badge>
                          <span className="font-medium">{customer.name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col text-xs">
                          {customer.email && <span>{customer.email}</span>}
                          {customer.phone && <span className="text-muted-foreground">{customer.phone}</span>}
                          {[customer.town, customer.city, customer.state].filter(Boolean).length > 0 && (
                            <span className="text-muted-foreground">{[customer.town, customer.city, customer.state].filter(Boolean).join(", ")}</span>
                          )}
                          {!customer.email && !customer.phone && !customer.town && !customer.city && !customer.state && "-"}
                        </div>
                      </TableCell>
                      <TableCell>{customer.tax_id || "-"}</TableCell>
                      <TableCell>
                        {customer.traccar_user_id ? (
                          <div className="flex items-center gap-1 text-xs" title={getTraccarUserLabel(customer.traccar_user_id)}>
                            <UserCheck className="h-3.5 w-3.5 text-green-500 shrink-0" />
                            <span className="truncate max-w-[6rem]">{getTraccarUserLabel(customer.traccar_user_id)}</span>
                          </div>
                        ) : (
                          <UserX className="h-3.5 w-3.5 text-muted-foreground" title={t("reseller.customer.noTraccarUser")} />
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Car className="h-4 w-4" />
                          {customer.traccar_user_id ? (deviceCounts[customer.traccar_user_id] ?? 0) : 0}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-0">
                          {profile?.stripe_account_id && customer.stripe_customer_id && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              title={t("reseller.stripe.billingPortal")}
                              onClick={() => billingPortalMutation.mutate(customer.id)}
                              disabled={billingPortalMutation.isPending}
                            >
                              {billingPortalMutation.isPending && billingPortalMutation.variables === customer.id ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <CreditCard className="h-3.5 w-3.5" />
                              )}
                            </Button>
                          )}
                          {customer.traccar_user_id && (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                title={t("users.loginAs")}
                                onClick={() => loginAsMutation.mutate(customer.traccar_user_id!)}
                                disabled={loginAsMutation.isPending}
                              >
                                {loginAsMutation.isPending && loginAsMutation.variables === customer.traccar_user_id ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <LogIn className="h-3.5 w-3.5" />
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                title={t("reseller.customer.manageDevices")}
                                onClick={() => { setDeviceSearch(""); setManageDevicesCustomer(customer); }}
                              >
                                <Car className="h-3.5 w-3.5" />
                              </Button>
                              {(() => {
                                const traccarUser = traccarUsers.find(u => u.id === customer.traccar_user_id);
                                return traccarUser ? (
                                  <EditUserGroupsDialog
                                    user={traccarUser}
                                    singleGroup
                                    trigger={
                                      <Button variant="ghost" size="icon" className="h-7 w-7" title={t("users.editGroups")}>
                                        <Layers className="h-3.5 w-3.5" />
                                      </Button>
                                    }
                                  />
                                ) : null;
                              })()}
                            </>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => openDialog(customer)}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => {
                              if (confirm(t("reseller.common.deleteConfirm"))) {
                                deleteMutation.mutate(customer.id);
                              }
                            }}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

      </div>

      {/* Manage devices dialog */}
      <Dialog open={!!manageDevicesCustomer} onOpenChange={(open) => { if (!open) setManageDevicesCustomer(null); }}>
        <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{manageDevicesCustomer?.name} — {t("reseller.customer.manageDevices")}</DialogTitle>
          </DialogHeader>
          <Input
            placeholder={t("devices.search")}
            value={deviceSearch}
            onChange={e => setDeviceSearch(e.target.value)}
            className="mt-2"
          />
          <div className="overflow-y-auto flex-1 border rounded mt-2">
            {allDevices
              .filter(d => d.name.toLowerCase().includes(deviceSearch.toLowerCase()))
              .map(device => {
                const linked = userDeviceIds.has(device.id);
                return (
                  <label
                    key={device.id}
                    className="flex items-center gap-3 px-3 py-2 hover:bg-muted cursor-pointer border-b last:border-0"
                  >
                    <input
                      type="checkbox"
                      checked={linked}
                      disabled={savingDevices}
                      onChange={() => handleToggleDevice(device.id, linked)}
                      className="h-4 w-4"
                    />
                    <span className="text-sm">{device.name}</span>
                    {device.uniqueId && !device.uniqueId.startsWith('not_installed_') && <span className="text-xs text-muted-foreground ml-auto">{device.uniqueId}</span>}
                  </label>
                );
              })}
          </div>
        </DialogContent>
      </Dialog>

    </DashboardLayout>
  );
}
